import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useCampaign, useNote, usePrefetchNote, useTypes } from '../hooks';
import { t } from '../i18n';
import { slugify, splitTarget } from '../util';
import { MarkdownView } from './MarkdownView';
import { useNewNote } from './NewNoteDialog';

const HOVER_DELAY = 350;

export function NoteLink(props: { cid: string; noteId: string; heading?: string; preview?: boolean; children: ReactNode }) {
  const { cid, noteId, heading, preview = true } = props;
  const prefetch = usePrefetchNote(cid);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    prefetch(noteId);
    if (!preview) return;
    const rect = e.currentTarget.getBoundingClientRect();
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setAnchor(rect), HOVER_DELAY);
  };
  const onLeave = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setAnchor(null), 150);
  };

  return (
    <>
      <Link
        className="wikilink"
        to={`/c/${cid}/n/${noteId}${heading ? `#${slugify(heading)}` : ''}`}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={() => prefetch(noteId)}
      >
        {props.children}
      </Link>
      {anchor &&
        createPortal(
          <HoverPreview
            cid={cid}
            noteId={noteId}
            anchor={anchor}
            onEnter={() => window.clearTimeout(timer.current)}
            onLeave={onLeave}
          />,
          document.body,
        )}
    </>
  );
}

function HoverPreview(props: { cid: string; noteId: string; anchor: DOMRect; onEnter: () => void; onLeave: () => void }) {
  const q = useNote(props.cid, props.noteId);
  const types = useTypes(props.cid);
  const campaign = useCampaign(props.cid);
  const gm = !!campaign.data && campaign.data.role !== 'player';
  const width = 420;
  const left = Math.min(Math.max(8, props.anchor.left), window.innerWidth - width - 8);
  const below = props.anchor.bottom + 320 < window.innerHeight;
  const style: React.CSSProperties = below
    ? { left, top: props.anchor.bottom + 6, width }
    : { left, bottom: window.innerHeight - props.anchor.top + 6, width };
  const type = q.data ? types.byKey.get(q.data.typeKey) : undefined;
  return (
    <div className="hover-preview" style={style} onMouseEnter={props.onEnter} onMouseLeave={props.onLeave}>
      {!q.data ? (
        <div className="muted">{t('common.loading')}</div>
      ) : (
        <>
          <div className="hover-title">
            <span className="type-dot" style={{ background: type?.color }} />
            {q.data.title}
          </div>
          <div className="hover-body">
            <MarkdownView note={q.data} isGameMaster={gm} preview />
          </div>
        </>
      )}
    </div>
  );
}

/** Unaufgelöster Link: Klick legt die Notiz an (Ordner aus dem Ziel, falls angegeben). */
export function UnresolvedLink(props: { cid: string; target: string; children: ReactNode }) {
  const newNote = useNewNote();
  const navigate = useNavigate();
  return (
    <a
      className="wikilink unresolved"
      href="#"
      title={t('note.unresolved')}
      onClick={(e) => {
        e.preventDefault();
        const { folder, title } = splitTarget(props.target);
        newNote.open({ title, folder, onCreated: (id) => navigate(`/c/${props.cid}/n/${id}?edit=1`) });
      }}
    >
      {props.children}
    </a>
  );
}
